import { useEffect, useState } from 'react';
import { useGetCustomers } from 'api/counterparties';
import {
  Customer,
  GetCustomerParams,
} from 'api/counterparties/counterparties.types';
import { AutocompletePaginationAsync } from 'components/Inputs';
import { NULL_VALUE } from 'pages/rates/components/RatesForm/RatesForm.consts';
import { FieldValues } from 'react-hook-form';
import { createHandleOnBlur, handleChange } from 'utils/form.utils';

export default function NamedAccountAutocomplete<
  TFieldValues extends FieldValues = FieldValues,
>({ formProps, error }: { formProps: TFieldValues; error: boolean }) {
  const { data, loading, getCustomers } = useGetCustomers();
  const [options, setOptions] = useState<Customer[]>([]);
  const [customersParams, setCustomersParams] = useState<GetCustomerParams>({
    filterText: '',
    pageNumber: 1,
  });

  useEffect(() => {
    getCustomers(customersParams);
  }, [getCustomers, customersParams]);

  useEffect(() => {
    if (data?.length) {
      const filteredCustomers = data.filter((customer) => {
        if (!customer.enabled) return false;
        const customerBusinessFunction = customer.businessFunctions?.find(
          (businessFunction) => businessFunction.__type === 'Customer',
        );
        return customerBusinessFunction?.enabled === true;
      });
      const fetchedOptions = filteredCustomers.map((customer) => ({
        ...customer,
      }));
      setOptions((prev) => [...prev, ...fetchedOptions]);
    }
  }, [data]);

  return (
    <AutocompletePaginationAsync
      {...formProps}
      padding='dense'
      width={280}
      value={formProps.field.value}
      multiple
      error={error}
      loading={loading}
      options={[
        {
          id: null,
          code: NULL_VALUE,
          enabled: true,
          lock: 0,
          comment: null,
          description: null,
          name: NULL_VALUE,
          address: { city: '', countryCode: '', lineOne: '', lineTwo: '' },
          taxId: { number: '', prefix: '', type: '' },
          businessCards: null,
          accounts: [],
          businessFunctions: [],
          fromEU: false,
          clientCharacteristics: null,
          defaultOwnInvoiceClass: null,
          tradeKeepers: null,
          dueDays: null,
        },
        ...options,
      ]}
      isOptionEqualToValue={(option, value) => value?.id === option?.id}
      getOptionLabel={(o) => o.code}
      handleSearchChange={handleChange(setOptions, setCustomersParams)}
      onChange={(_, data) =>
        formProps.field.onChange(data?.id === null ? [] : data)
      }
      onScrollBottom={() =>
        setCustomersParams((prev: any) => ({
          ...prev,
          pageNumber: prev.pageNumber + 1,
        }))
      }
      onBlur={createHandleOnBlur(
        setOptions,
        setCustomersParams,
        formProps.field.onBlur,
      )}
    />
  );
}
